import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { Spinner } from '@creditornot/cb-progress';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React, { useCallback, useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import { fetchKybSubmissionData, submitForReview } from 'apis/legitimizer/kyb/api';
import { Typography as T } from 'common/typography';

import { KybProcessContext } from './KybProcessContext';
import ReviewBankDetails from './reviewSections/ReviewBankDetails';
import ReviewBasicInfo from './reviewSections/ReviewBasicInfo';
import ReviewDocuments from './reviewSections/ReviewDocuments';
import ReviewPEP from './reviewSections/ReviewPEP';
import ReviewUbo from './reviewSections/ReviewUbo';
import SectionsLayout from './sections/SectionsLayout';

const Info = styled(T.Body2)`
  text-align: start;
  width: 100%;
`;

const Body = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-block-start: 50px;
`;

export const ActionsContainer = styled.div`
  display: flex;
  align-items: stretch;
  flex-direction: column;
  margin-block-start: 16px;
  gap: 16px;
  width: 100%;
`;

const BusinessVerificationReview = () => {
  const { processId, merchantId } = useContext(KybProcessContext);

  const { data: submissionResponse = null, isLoading } = useQuery(
    ['/kyb/processes/submissions', processId],
    () => fetchKybSubmissionData(processId),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: false,
    },
  );

  const { locale: currentLocale } = useIntl();
  const [submitting, setSubmitting] = useState(false);
  const [hasMissingFields, setHasMissingFields] = useState(false);
  const [validationError, setValidationError] = useState<AxiosError>();

  const history = useHistory();
  const handleSubmit = useCallback(() => {
    setSubmitting(true);
    submitForReview(processId, currentLocale)
      .then(() => history.push(`/merchant/${merchantId}/business-verification/success`))
      .catch(e => setValidationError(e))
      .finally(() => setSubmitting(false));
  }, [currentLocale, history, merchantId, processId]);

  const handleStepChange = (section: string) =>
    history.push(`/merchant/${merchantId}/business-verification/flow/${section}`);

  if (!submissionResponse?.data || isLoading) {
    return (
      <SpinnerContainer>
        <Spinner size={'large'} />
      </SpinnerContainer>
    );
  }

  return (
    <SectionsLayout onStepChange={handleStepChange}>
      <Info>
        <FormattedMessage id="compliance.kyb.sections.review.subtitle" />
      </Info>

      <Body>
        <ReviewBasicInfo
          submissionData={submissionResponse.data}
          onEditData={handleStepChange}
          setHasMissingFields={setHasMissingFields}
        />
        <ReviewDocuments
          submissionDocuments={submissionResponse.documents}
          submissionData={submissionResponse.data}
          onEditData={handleStepChange}
          setHasMissingFields={setHasMissingFields}
        />
        <ReviewUbo
          submissionData={submissionResponse.data}
          onEditData={handleStepChange}
          setHasMissingFields={setHasMissingFields}
        />
        <ReviewPEP
          submissionData={submissionResponse.data}
          onEditData={handleStepChange}
          setHasMissingFields={setHasMissingFields}
        />
        <ReviewBankDetails
          submissionData={submissionResponse.data}
          submissionDocuments={submissionResponse.documents}
          onEditData={handleStepChange}
          setHasMissingFields={setHasMissingFields}
        />

        <ActionsContainer>
          {validationError?.response && (
            <Alert data-test-id="basic-info-validation-error" variant="error">
              <FormattedMessage id="errors.unknown-error-retry-contact" />
            </Alert>
          )}

          {hasMissingFields && (
            <Alert variant="error">
              <FormattedMessage id="errors.missing-fields-error-label" />
            </Alert>
          )}

          <Button
            size="large"
            variant="blue"
            loading={submitting}
            disabled={submitting || !!validationError || hasMissingFields}
            onClick={handleSubmit}
          >
            <FormattedMessage id="compliance.kyb.sections.bank-details.send-for-review" />
          </Button>
        </ActionsContainer>
      </Body>
    </SectionsLayout>
  );
};

export default BusinessVerificationReview;
